import { useState, useEffect } from 'react'
import { FilterRule } from '@/components/ui/advanced-filter'

export interface LinkedInView {
  id: string
  name: string
  isDefault: boolean
  filters: FilterRule[]
  visibleColumns: string[]
  searchQuery: string
  statusFilter: string
  clientFilter: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  createdAt: Date
  updatedAt: Date
}

const DEFAULT_COLUMNS = [
  'sender_name', 'linkedin_url', 'client_name', 'daily_connections', 'daily_inmails', 
  'daily_direct_messages', 'connections_accepted', 'direct_messages_sent', 'inmails_sent', 
  'sync_status', 'platform_status', 'last_synced_at'
]

const DEFAULT_VIEW: LinkedInView = {
  id: 'default',
  name: 'Default View',
  isDefault: true,
  filters: [],
  visibleColumns: DEFAULT_COLUMNS,
  searchQuery: '',
  statusFilter: 'all',
  clientFilter: 'all',
  createdAt: new Date(),
  updatedAt: new Date()
}

export function useLinkedInViewSettings() {
  const [views, setViews] = useState<LinkedInView[]>([DEFAULT_VIEW])
  const [currentViewId, setCurrentViewId] = useState<string>('default')

  // Load views from localStorage on mount
  useEffect(() => {
    const savedViews = localStorage.getItem('linkedin-views')
    const savedCurrentViewId = localStorage.getItem('linkedin-current-view-id')
    
    if (savedViews) {
      try {
        const parsedViews = JSON.parse(savedViews).map((view: any) => ({
          ...view,
          createdAt: new Date(view.createdAt),
          updatedAt: new Date(view.updatedAt)
        }))
        setViews(parsedViews)
      } catch (error) {
        console.error('Failed to parse saved LinkedIn views:', error)
      }
    }
    
    if (savedCurrentViewId) {
      setCurrentViewId(savedCurrentViewId)
    }
  }, [])

  // Save views to localStorage when they change
  useEffect(() => {
    localStorage.setItem('linkedin-views', JSON.stringify(views))
  }, [views])

  // Save current view ID when it changes
  useEffect(() => {
    localStorage.setItem('linkedin-current-view-id', currentViewId)
  }, [currentViewId])

  const currentView = views.find(view => view.id === currentViewId) || DEFAULT_VIEW

  const createView = (name: string, settings: Partial<LinkedInView>) => {
    const newView: LinkedInView = {
      id: `view-${Date.now()}`,
      name,
      isDefault: false,
      filters: [],
      visibleColumns: DEFAULT_COLUMNS,
      searchQuery: '',
      statusFilter: 'all',
      clientFilter: 'all',
      createdAt: new Date(),
      updatedAt: new Date(),
      ...settings
    }
    
    setViews(prev => [...prev, newView])
    setCurrentViewId(newView.id)
    return newView.id
  }

  const updateView = (viewId: string, updates: Partial<LinkedInView>) => {
    setViews(prev => prev.map(view => 
      view.id === viewId 
        ? { ...view, ...updates, updatedAt: new Date() }
        : view
    ))
  }

  const deleteView = (viewId: string) => {
    if (viewId === 'default') return // Can't delete default view
    
    setViews(prev => prev.filter(view => view.id !== viewId))
    
    // If deleting current view, switch to default
    if (currentViewId === viewId) {
      setCurrentViewId('default')
    }
  }

  const setDefaultView = (viewId: string) => {
    setViews(prev => prev.map(view => ({
      ...view,
      isDefault: view.id === viewId
    })))
  }

  const duplicateView = (viewId: string, newName: string) => {
    const sourceView = views.find(view => view.id === viewId)
    if (!sourceView) return
    
    const newView: LinkedInView = {
      ...sourceView,
      id: `view-${Date.now()}`,
      name: newName,
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    
    setViews(prev => [...prev, newView])
    return newView.id
  }

  return {
    views,
    currentView,
    currentViewId,
    setCurrentViewId,
    createView,
    updateView,
    deleteView,
    setDefaultView,
    duplicateView
  }
}